<script lang="ts" setup>
import {ref, shallowRef, computed, watch} from 'vue'
import {useAppStore} from '@/stores/app'
import {useToast} from '@/composables/useToast'
import {folderOptions} from '@/composables/useTaskCreateForm'
import {dateValueToApiString, jsDateToDateValue} from '@/composables/useDateConversion'
import type {Project, TaskFolder} from '@backend/types'
import {CalendarDateTime, type DateValue} from '@internationalized/date'
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Separator} from '@/components/ui/separator'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Calendar} from '@/components/ui/calendar'
import {CalendarDays, X} from 'lucide-vue-next'

const props = defineProps<{
  open: boolean
  folder?: string
  project?: Project
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const appStore = useAppStore()
const toast = useToast()

const createFolderOptions = folderOptions.filter((f) => f !== 'trash')

const title = ref('')
const notes = ref('')
const folder = ref<TaskFolder>('inbox')
const dueDate = shallowRef<DateValue | undefined>()
const isSaving = ref(false)
const discardDialogOpen = ref(false)
const dueDatePopoverOpen = ref(false)

const isEditMode = computed(() => !!props.project)
const canSave = computed(() => title.value.trim().length >= 1 && !isSaving.value)

const dueTime = computed({
  get() {
    if (!dueDate.value || !('hour' in dueDate.value)) return '09:00'
    return `${String(dueDate.value.hour).padStart(2, '0')}:${String(dueDate.value.minute).padStart(2, '0')}`
  },
  set(val: string) {
    if (!dueDate.value) return
    const [h, m] = val.split(':').map(Number)
    dueDate.value = new CalendarDateTime(dueDate.value.year, dueDate.value.month, dueDate.value.day, h ?? 9, m ?? 0)
  },
})

function getDefaultFolder(): TaskFolder {
  const valid: TaskFolder[] = ['inbox', 'next', 'scheduled', 'waiting', 'someday', 'reference', 'archived']
  if (props.folder && valid.includes(props.folder as TaskFolder)) {
    return props.folder as TaskFolder
  }
  return 'inbox'
}

function resetForm() {
  title.value = ''
  notes.value = ''
  folder.value = getDefaultFolder()
  dueDate.value = undefined
}

function populateFromProject() {
  const p = props.project!
  title.value = p.title
  notes.value = p.notes || ''
  folder.value = p.folder
  dueDate.value = p.due_date ? jsDateToDateValue(new Date(p.due_date)) : undefined
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      if (isEditMode.value) {
        populateFromProject()
      } else {
        resetForm()
      }
    }
  }
)

function handleClose() {
  if (title.value.trim()) {
    discardDialogOpen.value = true
  } else {
    resetForm()
    emit('update:open', false)
  }
}

function discardAndClose() {
  discardDialogOpen.value = false
  resetForm()
  emit('update:open', false)
}

function selectDueDate(value: DateValue | undefined) {
  if (value) {
    const [h, m] = dueTime.value.split(':').map(Number)
    dueDate.value = new CalendarDateTime(value.year, value.month, value.day, h ?? 9, m ?? 0)
  } else {
    dueDate.value = undefined
  }
  dueDatePopoverOpen.value = false
}

function formatDate(date: DateValue): string {
  return `${date.day.toString().padStart(2, '0')}.${date.month.toString().padStart(2, '0')}.${date.year}`
}

async function handleSave() {
  if (!canSave.value) return
  isSaving.value = true
  try {
    const projectData: Partial<Project> = {
      title: title.value.trim(),
      notes: notes.value,
      folder: folder.value,
      due_date: dueDate.value ? dateValueToApiString(dueDate.value) : undefined,
    }

    if (isEditMode.value && props.project) {
      await appStore.updateProject(props.project.project_id, projectData)
      toast.success('Project updated')
    } else {
      await appStore.createProject(projectData)
      toast.success('Project created')
    }

    resetForm()
    emit('update:open', false)
  } catch (error) {
    toast.error(isEditMode.value ? 'Failed to update project' : 'Failed to create project')
    console.error('Project save error:', error)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogScrollContent class="max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ isEditMode ? $t('actions.edit_project') : $t('actions.new_project') }}</DialogTitle>
        <DialogDescription class="sr-only">{{
          isEditMode ? $t('actions.edit_project') : $t('actions.new_project')
        }}</DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-2">
        <!-- Title -->
        <div class="grid gap-2">
          <Label for="project-title">{{ $t('fields.title') }}</Label>
          <Input id="project-title" v-model="title" :placeholder="$t('fields.title')" />
        </div>

        <!-- Notes -->
        <div class="grid gap-2">
          <Label for="project-notes">{{ $t('fields.notes') }}</Label>
          <Textarea id="project-notes" v-model="notes" rows="3" :placeholder="$t('fields.notes')" />
        </div>

        <Separator />

        <!-- Folder -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.folder') }}</Label>
          <Select v-model="folder">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="f in createFolderOptions" :key="f" :value="f">
                {{ $t('scopes.' + f) }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Due Date -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.due_date') }}</Label>
          <div class="flex gap-2">
            <Popover v-model:open="dueDatePopoverOpen">
              <PopoverTrigger as-child>
                <Button
                  variant="outline"
                  class="flex-1 justify-start text-left font-normal"
                  :class="{'text-muted-foreground': !dueDate}">
                  <CalendarDays class="mr-2 h-4 w-4" />
                  <span v-if="dueDate">{{ formatDate(dueDate) }}</span>
                  <span v-else>{{ $t('fields.due_date') }}</span>
                  <button
                    v-if="dueDate"
                    type="button"
                    class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                    @click.stop="dueDate = undefined">
                    <X class="h-3 w-3" />
                  </button>
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0" align="start">
                <Calendar :model-value="dueDate" locale="ru" :week-starts-on="1" @update:model-value="selectDueDate" />
              </PopoverContent>
            </Popover>
            <Input v-if="dueDate" v-model="dueTime" type="time" class="w-[7rem]" />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="handleClose">{{ $t('actions.cancel') }}</Button>
        <Button :disabled="!canSave" @click="handleSave">{{ $t('actions.save') }}</Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <!-- Discard confirmation -->
  <AlertDialog :open="discardDialogOpen" @update:open="discardDialogOpen = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('messages.unsaved_changes') }}</AlertDialogTitle>
        <AlertDialogDescription>{{ $t('messages.unsaved_changes_confirm') }}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ $t('actions.continue_editing') }}</AlertDialogCancel>
        <AlertDialogAction @click="discardAndClose">{{ $t('actions.discard') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
